import React, { useCallback, useContext, useMemo, useState } from "react";
import { format } from "date-fns";
import { UserContext } from "../../../../contexts/CurrentUserContext";
import Stepper from "./components/Stepper/Stepper";
import OperationsStep from "./components/OperationsStep/OperationsStep";
import RecipientsStep from "./components/RecipientsStep/RecipientsStep";
import BaseStep from "./components/BaseStep/BaseStep";
import ReviewStep from "./components/ReviewStep/ReviewStep";
import ResultStep from "./components/ResultStep/ResultStep";
import HistoryTab from "./components/HistoryTab/HistoryTab";
import Toast from "../shared/recognition/ui/Toast";
import ConfirmDialog from "../shared/recognition/ui/ConfirmDialog";
import { formatAmount } from "../OperationsJournal/mock/operationsJournalMock";
import { RECIPIENT_PROJECTS } from "./mock/recipientProjectsMock";
import { INITIAL_ALLOCATION_HISTORY } from "./mock/allocationHistoryMock";
import { EMPTY_BASE, calculateAllocation } from "./utils/allocationBase";
import { buildAllocationRecord } from "./utils/allocationRecord";
import { ALLOCATION_STEPS, ALLOCATION_TABS, ONLY_THIS_RECORD_NOTE } from "./constants";
import "./Allocation.css";

const RESULT_STEP = ALLOCATION_STEPS.length - 1;
const REVIEW_STEP = RESULT_STEP - 1;

function Allocation() {
  const [activeTab, setActiveTab] = useState("new");
  const [step, setStep] = useState(0);
  const [wizardKey, setWizardKey] = useState(0);
  const [selectedOperations, setSelectedOperations] = useState(() => new Map());
  const [selectedProjects, setSelectedProjects] = useState(() => new Set());
  const [base, setBase] = useState(EMPTY_BASE);
  const [history, setHistory] = useState(INITIAL_ALLOCATION_HISTORY);
  const [createdRecord, setCreatedRecord] = useState(null);
  const [restartRecord, setRestartRecord] = useState(null);
  const [pendingRestart, setPendingRestart] = useState(null);
  const cancelPendingRestart = useCallback(() => setPendingRestart(null), []);
  const [toast, setToast] = useState(null);
  const closeToast = useCallback(() => setToast(null), []);
  const { userData } = useContext(UserContext);
  const authorName = userData
    ? `${userData.surname || ""} ${(userData.name || "").charAt(0)}.`.trim()
    : "Пользователь";

  const operations = useMemo(() => [...selectedOperations.values()], [selectedOperations]);
  const operationsTotal = operations.reduce((sum, row) => sum + (row.amount || 0), 0);
  const recipientProjects = useMemo(
    () => RECIPIENT_PROJECTS.filter((project) => selectedProjects.has(project.id)),
    [selectedProjects]
  );
  const allocation = useMemo(
    () => calculateAllocation(recipientProjects, operationsTotal, base),
    [recipientProjects, operationsTotal, base]
  );

  const showToast = (title, text) => setToast({ id: Date.now(), title, text });

  const loadWizard = ({ operationsMap, projectIds, nextBase, nextStep, restartOf }) => {
    setSelectedOperations(operationsMap);
    setSelectedProjects(projectIds);
    setBase(nextBase);
    setCreatedRecord(null);
    setRestartRecord(restartOf);
    setStep(nextStep);
    setWizardKey((key) => key + 1);
  };

  const startNewAllocation = () =>
    loadWizard({
      operationsMap: new Map(),
      projectIds: new Set(),
      nextBase: EMPTY_BASE,
      nextStep: 0,
      restartOf: null,
    });

  /** Мастер заполняется параметрами записи и открывается на проверке — их можно сразу подтвердить или изменить */
  const startRestart = (record) => {
    loadWizard({
      operationsMap: new Map(record.params.operations.map((operation) => [operation.id, operation])),
      projectIds: new Set(record.params.projectIds),
      nextBase: record.params.base,
      nextStep: REVIEW_STEP,
      restartOf: record,
    });
    setActiveTab("new");
  };

  /** Если в мастере есть несохранённые изменения, перезапуск сначала спрашивает подтверждение */
  const requestRestart = (record) => {
    if (restartRecord?.id === record.id && step !== RESULT_STEP) {
      setActiveTab("new");
      return;
    }
    const hasUnsavedWizard =
      step !== RESULT_STEP &&
      (Boolean(restartRecord) ||
        selectedOperations.size > 0 ||
        selectedProjects.size > 0 ||
        base !== EMPTY_BASE);
    if (hasUnsavedWizard) setPendingRestart(record);
    else startRestart(record);
  };

  const createAllocation = () => {
    const now = new Date();
    const record = buildAllocationRecord({
      id: restartRecord
        ? restartRecord.id
        : history.reduce((max, item) => Math.max(max, item.id), 0) + 1,
      date: format(now, "dd.MM.yyyy"),
      time: format(now, "HH:mm"),
      author: authorName,
      operations,
      allocation,
      base,
      restarted: Boolean(restartRecord),
    });

    if (restartRecord) {
      setHistory((prev) => prev.map((item) => (item.id === record.id ? record : item)));
      showToast(
        `Распределение ${record.document} перезапущено`,
        `Удалено проводок: ${restartRecord.entries.length}, создано: ${record.entries.length}`
      );
    } else {
      setHistory((prev) => [record, ...prev]);
    }
    setRestartRecord(null);
    setCreatedRecord(record);
    setStep(RESULT_STEP);
  };

  const deleteRecord = (record) => {
    setHistory((prev) => prev.filter((item) => item.id !== record.id));
    if (restartRecord?.id === record.id) startNewAllocation();
    showToast(
      `Распределение ${record.document} удалено`,
      `Удалено проводок: ${record.entries.length}. ${ONLY_THIS_RECORD_NOTE}`
    );
  };

  return (
    <div className="al">
      <div className="al-card">
        <div className="al-tabs" role="tablist">
          {ALLOCATION_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              className={`al-tabs__tab${activeTab === tab.id ? " is-active" : ""}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === "new" && (
          <Stepper
            steps={ALLOCATION_STEPS}
            current={step}
            locked={step === RESULT_STEP}
            onChange={setStep}
          />
        )}
      </div>

      {activeTab === "history" && (
        <HistoryTab history={history} onRestart={requestRestart} onDelete={deleteRecord} />
      )}

      <div hidden={activeTab !== "new"} key={wizardKey}>
        {restartRecord && (
          <div className="al-restart-banner">
            <div>
              <strong>Перезапуск распределения {restartRecord.document}</strong> от{" "}
              {restartRecord.date} · {restartRecord.period}. Параметры можно оставить или
              изменить на любом шаге. Проводки этого запуска будут удалены и созданы заново.{" "}
              {ONLY_THIS_RECORD_NOTE}
            </div>
            <button type="button" className="al-link" onClick={startNewAllocation}>
              Отменить перезапуск
            </button>
          </div>
        )}

        <div hidden={step !== 0}>
          <OperationsStep
            selected={selectedOperations}
            onSelectedChange={setSelectedOperations}
            onContinue={() => setStep(1)}
          />
        </div>
        <div hidden={step !== 1}>
          <RecipientsStep
            operationsCount={operations.length}
            operationsTotalLabel={formatAmount(operationsTotal)}
            selected={selectedProjects}
            onSelectedChange={setSelectedProjects}
            onBack={() => setStep(0)}
            onContinue={() => setStep(2)}
          />
        </div>
        {step === 2 && (
          <BaseStep
            projects={recipientProjects}
            totalAmount={operationsTotal}
            operationsCount={operations.length}
            value={base}
            onChange={setBase}
            onBack={() => setStep(1)}
            onContinue={() => setStep(REVIEW_STEP)}
          />
        )}
        {step === REVIEW_STEP && (
          <ReviewStep
            operations={operations}
            totalAmount={operationsTotal}
            base={base}
            allocation={allocation}
            restartOf={restartRecord}
            onBack={() => setStep(2)}
            onConfirm={createAllocation}
          />
        )}
        {step === RESULT_STEP && createdRecord && (
          <ResultStep
            record={createdRecord}
            onNewAllocation={startNewAllocation}
            onOpenHistory={() => setActiveTab("history")}
          />
        )}
      </div>

      <ConfirmDialog
        isOpen={Boolean(pendingRestart)}
        title={`Перезапустить ${pendingRestart?.document || ""}?`}
        text={
          restartRecord
            ? `Перезапуск ${restartRecord.document} ещё не подтверждён — его изменения будут потеряны.`
            : "В мастере есть незавершённое распределение — выбранные операции, проекты и база будут сброшены."
        }
        confirmLabel="Перезапустить"
        onCancel={cancelPendingRestart}
        onConfirm={() => {
          const record = pendingRestart;
          setPendingRestart(null);
          startRestart(record);
        }}
      />
      <Toast toast={toast} onClose={closeToast} />
    </div>
  );
}

export default Allocation;
